import { reservationKind } from '../../shared/autoPublish';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { ThreadAccountPostReservation, ThreadAccountPostResponse } from '../../api/types';
import { Button } from '../../shared/components/Button';
import { seoulDate } from '../postInsights';
import { editableReservation, reservationStatusLabel } from './reservationState';

export function ReservationCalendar({ tasks, posts, onEdit }: {
  tasks: ThreadAccountPostReservation[];
  posts: ThreadAccountPostResponse[];
  onEdit: (task: ThreadAccountPostReservation) => void;
}) {
  const today = seoulDate();
  const [month, setMonth] = useState(today.slice(0, 7));
  const [selectedDate, setSelectedDate] = useState(today);
  const [year, monthNumber] = month.split('-').map(Number);
  const firstDay = new Date(Date.UTC(year, monthNumber - 1, 1)).getUTCDay();
  const dayCount = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const taskCounts = useMemo(() => tasks.reduce<Record<string, number>>((map, task) => {
    if (task.scheduledAt) {
      const date = task.scheduledAt.slice(0, 10);
      map[date] = (map[date] ?? 0) + 1;
    }
    return map;
  }, {}), [tasks]);
  const postCounts = useMemo(() => posts.filter((p) => p.status === 'REGISTERED').reduce<Record<string, number>>((map, post) => {
    if (post.registeredAt) { const date = post.registeredAt.slice(0, 10); map[date] = (map[date] ?? 0) + 1; }
    return map;
  }, {}), [posts]);
  const changeMonth = (step: number) => {
    const date = new Date(Date.UTC(year, monthNumber - 1 + step, 1)).toISOString().slice(0, 10);
    setMonth(date.slice(0, 7));
    setSelectedDate(date);
  };
  const selectedTasks = tasks.filter((task) => task.scheduledAt?.slice(0, 10) === selectedDate)
    .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
  const selectedPosts = posts.filter((post) => post.status === 'REGISTERED' && post.registeredAt?.slice(0, 10) === selectedDate)
    .sort((a, b) => (a.registeredAt ?? '').localeCompare(b.registeredAt ?? ''));
  const unscheduledCount = tasks.filter((task) => !task.scheduledAt).length;
  return (
    <Panel aria-label="예약 캘린더">
      <Heading>
        <div><h2>예약 캘린더</h2><Month aria-live="polite">{year}년 {monthNumber}월</Month></div>
        <Navigation aria-label="캘린더 월 이동">
          <Button type="button" $size="sm" $variant="ghost" aria-label="이전 달" onClick={() => changeMonth(-1)}>‹</Button>
          <Button type="button" $size="sm" $variant="secondary" onClick={() => { setMonth(today.slice(0, 7)); setSelectedDate(today); }}>오늘</Button>
          <Button type="button" $size="sm" $variant="ghost" aria-label="다음 달" onClick={() => changeMonth(1)}>›</Button>
        </Navigation>
      </Heading>
      <Help>한국 시간 기준입니다. 날짜를 선택해 예약과 발행한 글을 확인하세요.</Help>
      <Grid>
        {['일', '월', '화', '수', '목', '금', '토'].map((day) => <Weekday key={day}>{day}</Weekday>)}
        {Array.from({ length: firstDay }, (_, i) => <div aria-hidden="true" key={`blank-${i}`} />)}
        {Array.from({ length: dayCount }, (_, i) => {
          const date = `${month}-${String(i + 1).padStart(2, '0')}`;
          return <Day key={date} type="button" $selected={date === selectedDate} aria-pressed={date === selectedDate}
            aria-current={date === today ? 'date' : undefined}
            aria-label={`${date}${date === today ? ' 오늘' : ''} 예약 ${taskCounts[date] ?? 0}건 발행 ${postCounts[date] ?? 0}건`}
            onClick={() => setSelectedDate(date)}>
            <DayNumber $today={date === today}>{i + 1}</DayNumber>
            {taskCounts[date] > 0 && <DayCount>예약 {taskCounts[date]}</DayCount>}
            {postCounts[date] > 0 && <DayCount $published>발행 {postCounts[date]}</DayCount>}
          </Day>;
        })}
      </Grid>
      <Agenda aria-live="polite">
        <AgendaHeading>{selectedDate.replace(/-/g, '. ')} <span>예약 {selectedTasks.length}건 · 발행 {selectedPosts.length}건</span></AgendaHeading>
        {selectedTasks.map((task) => <Entry key={task.id}>
          <EntryContent><EntryMeta>{task.scheduledAt?.slice(11, 16)} · {reservationKind(task)} · {reservationStatusLabel(task.status)}</EntryMeta><EntryTitle>{task.subject}</EntryTitle></EntryContent>
          {editableReservation(task) && <Button type="button" $size="sm" $variant="secondary" onClick={() => onEdit(task)}>초안 열기</Button>}
        </Entry>)}
        {selectedPosts.map((post) => <Entry key={`post-${post.id}`}>
          <EntryContent><EntryMeta>{post.registeredAt?.slice(11, 16)} · 발행 완료</EntryMeta><EntryTitle>{post.post}</EntryTitle></EntryContent>
          {post.threadPostUrl && <PostLink href={post.threadPostUrl} target="_blank" rel="noreferrer">글 보기 ↗</PostLink>}
        </Entry>)}
        {!selectedTasks.length && !selectedPosts.length && <Empty>이 날짜에는 예약 작업과 발행한 글이 없습니다.</Empty>}
      </Agenda>
      {unscheduledCount > 0 && <Help>시각 미지정 작업 {unscheduledCount}건은 아래 보드에서 확인할 수 있습니다.</Help>}
    </Panel>
  );
}

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 24px;
  margin-bottom: 24px;
  @media (max-width: 600px) { padding: 16px 12px; }
`;
const Heading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  h2 { margin: 0 0 6px; font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.textSecondary}; }
`;
const Month = styled.p`margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.03em;`;
const Navigation = styled.nav`display: flex; align-items: center; gap: 4px; button { min-width: 44px; }`;
const Help = styled.p`font-size: 13px; line-height: 1.7; color: ${({ theme }) => theme.colors.textSecondary}; margin: 12px 0;`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 5px; @media (max-width: 600px) { gap: 3px; }`;
const Weekday = styled.div`text-align: center; font-size: 12px; font-weight: 600; padding: 10px 0; color: ${({ theme }) => theme.colors.textSecondary};`;
const Day = styled.button<{ $selected: boolean }>`
  min-width: 0; min-height: 100px; display: flex; flex-direction: column; align-items: flex-start; gap: 5px;
  text-align: left; padding: 8px; cursor: pointer; border-radius: 8px;
  border: 1px solid ${({ theme, $selected }) => $selected ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $selected }) => $selected ? theme.colors.primarySoft : theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-shadow: ${({ theme, $selected }) => $selected ? `inset 0 0 0 1px ${theme.colors.primary}` : 'none'};
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
  @media (max-width: 600px) { align-items: center; padding: 5px 1px; min-height: 88px; gap: 4px; }
`;
const DayNumber = styled.strong<{ $today: boolean }>`
  display: grid; place-items: center; min-width: 25px; height: 25px; border-radius: 50%; font-size: 13px;
  background: ${({ theme, $today }) => $today ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $today }) => $today ? theme.colors.onPrimary : 'inherit'};
`;
const DayCount = styled.span<{ $published?: boolean }>`
  font-size: 12px; font-weight: 600; color: ${({ theme, $published }) => $published ? theme.colors.success : theme.colors.primary};
  @media (max-width: 600px) { font-size: 10px; letter-spacing: -0.04em; }
`;
const Agenda = styled.div`margin-top: 20px; border-top: 1px solid ${({ theme }) => theme.colors.border}; padding-top: 18px;`;
const AgendaHeading = styled.h3`
  margin: 0 0 10px; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 15px;
  span { font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary}; font-weight: 500; }
`;
const Entry = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child { border-bottom: 0; }
  button, a { flex-shrink: 0; }
`;
const EntryContent = styled.div`min-width: 0; display: grid; gap: 5px;`;
const EntryMeta = styled.span`font-size: 12px; line-height: 1.6; color: ${({ theme }) => theme.colors.textSecondary};`;
const EntryTitle = styled.p`margin: 0; font-size: 14px; line-height: 1.6; overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;`;
const PostLink = styled.a`display: inline-flex; align-items: center; min-height: 44px; font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.colors.primary};`;
const Empty = styled.p`padding: 20px 12px; margin: 0; text-align: center; font-size: 13px; line-height: 1.7; color: ${({ theme }) => theme.colors.textSecondary}; background: ${({ theme }) => theme.colors.surfaceMuted}; border-radius: 8px;`;
